import React, { useState, useEffect, useRef } from 'react';
import CodeEditor, { EditorRef } from './CodeEditor';
import { languages } from '../data/languages';
import codeReviewService, { CodeReviewScore } from '../services/CodeReviewService';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import type { ComponentPropsWithoutRef } from 'react';
import { motion } from 'framer-motion';

// Icons
const CheckIcon = () => (
  <svg className="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = () => (
  <svg className="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const LoadingIcon = () => (
  <svg className="w-4 h-4 inline-block animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

type Language = keyof typeof languages;

type Difficulty = 'Easy' | 'Medium' | 'Hard';

interface PracticeProblemProps {
  problem: {
    id: string;
    title: string;
    difficulty: Difficulty;
    description: string;
    examples: Array<{
      input: string;
      output: string;
      explanation?: string;
    }>;
    starterCode: Record<string, string>;
    testCases: Array<{
      input: string;
      output: string;
    }>;
  };
  onBack: () => void;
}

// Markdown components configuration
const markdownComponents: Components = {
  code({ inline, className, children, ...props }: ComponentPropsWithoutRef<'code'> & { inline?: boolean }) {
    const match = /language-(\w+)/.exec(className || '');
    return !inline ? (
      <div className="relative">
        <pre className="!bg-black/30 !p-4 !rounded-lg !mt-2 !mb-4 overflow-x-auto border border-white/5">
          <code className={`${match ? `language-${match[1]}` : ''} !font-mono !text-sm !text-white/80`} {...props}>
            {String(children).replace(/\n$/, '')}
          </code>
        </pre>
      </div>
    ) : (
      <code className="!bg-black/30 !px-2 !py-1 !rounded !text-sm !font-mono !text-white/80" {...props}>
        {children}
      </code>
    );
  },
  h1(props: ComponentPropsWithoutRef<'h1'>) {
    return <h1 className="!text-2xl !font-normal !mt-8 !mb-6 !text-white !tracking-wide" {...props} />;
  },
  h2(props: ComponentPropsWithoutRef<'h2'>) {
    return <h2 className="!text-xl !font-normal !mt-7 !mb-4 !text-white !tracking-wide" {...props} />;
  },
  h3(props: ComponentPropsWithoutRef<'h3'>) {
    return <h3 className="!text-lg !font-normal !mt-6 !mb-3 !text-white !tracking-wide" {...props} />;
  },
  p(props: ComponentPropsWithoutRef<'p'>) {
    return <p className="!text-white/80 !leading-7 !my-6 !text-base" {...props} />;
  },
  ul(props: ComponentPropsWithoutRef<'ul'>) {
    return <ul className="!list-disc !pl-8 !my-6 !text-white/80 !space-y-2" {...props} />;
  },
  ol(props: ComponentPropsWithoutRef<'ol'>) {
    return <ol className="!list-decimal !pl-8 !my-6 !text-white/80 !space-y-2" {...props} />;
  },
  li(props: ComponentPropsWithoutRef<'li'>) {
    return <li className="!text-base !leading-7" {...props} />;
  },
  a(props: ComponentPropsWithoutRef<'a'>) {
    return <a className="!text-white/80 !no-underline hover:!underline !transition-colors" {...props} />;
  },
  blockquote(props: ComponentPropsWithoutRef<'blockquote'>) {
    return <blockquote className="!border-l-4 !border-white/10 !pl-6 !my-6 !text-white/80 !italic" {...props} />;
  },
  table(props: ComponentPropsWithoutRef<'table'>) {
    return (
      <div className="!overflow-x-auto !my-8">
        <table className="!min-w-full !border-collapse !text-sm" {...props} />
      </div>
    );
  },
  th(props: ComponentPropsWithoutRef<'th'>) {
    return <th className="!border !border-white/10 !bg-black/30 !px-4 !py-3 !text-left !font-normal" {...props} />;
  },
  td(props: ComponentPropsWithoutRef<'td'>) {
    return <td className="!border !border-white/10 !px-4 !py-3 !text-white/80" {...props} />;
  },
};

export const PracticeProblem: React.FC<PracticeProblemProps> = ({ problem, onBack }) => {
  const editorRef = useRef<EditorRef | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState(40); // percentage
  const [editorHeight, setEditorHeight] = useState(60); // percentage
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('python');
  const [code, setCode] = useState(problem.starterCode[selectedLanguage] || '');
  const [output, setOutput] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState<'output' | 'feedback'>('output');
  const [reviewScores, setReviewScores] = useState<CodeReviewScore | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeType, setResizeType] = useState<'horizontal' | 'vertical' | 'output'>('horizontal');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Editor settings
  const [editorFontSize, setEditorFontSize] = useState('14px');
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [enableAutoCompletion, setEnableAutoCompletion] = useState(true);
  const [enableSyntaxHighlighting, setEnableSyntaxHighlighting] = useState(true);

  // Create a forceUpdate function to ensure UI updates
  const [, forceUpdate] = useState<{}>({});
  const forceRender = () => forceUpdate({});

  // Debounce function for resize operations
  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout | null = null;
    return (...args: any[]) => {
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => {
        func(...args);
      }, wait);
    };
  };

  // Debounced setters to avoid too many rapid state changes
  const debouncedSetLeftPanelWidth = debounce(setLeftPanelWidth, 5);
  const debouncedSetEditorHeight = debounce(setEditorHeight, 5);

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLanguage = e.target.value as Language;
    setSelectedLanguage(newLanguage);
    
    // Get starter code for the selected language
    const starterCode = problem.starterCode[newLanguage] || '';
    setCode(starterCode);
  };

  const startResize = (type: 'horizontal' | 'vertical' | 'output') => {
    setIsResizing(true);
    setResizeType(type);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing || !containerRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    
    if (resizeType === 'horizontal') {
      const newLeftWidth = ((e.clientX - containerRect.left) / containerWidth) * 100;
      setLeftPanelWidth(Math.max(20, Math.min(80, newLeftWidth)));
    } else if (resizeType === 'vertical') {
      const newEditorHeight = ((e.clientY - containerRect.top) / containerHeight) * 100;
      setEditorHeight(Math.max(30, Math.min(70, newEditorHeight)));
    }
  };

  const stopResize = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopResize);
    };
  }, [isResizing, resizeType]);

  const runTestCase = async (code: string, testCase: { input: string; output: string }): Promise<string | boolean> => {
    const apiKey = 'Bearer sk_live_43yY02IpfdzUfeQ8XXSk3dplnAWVygfE';

    try {
      // Format input and output for comparison
      const formattedInput = testCase.input.replace(/\[|\]/g, '').trim();
      const formattedOutput = testCase.output.replace(/\[|\]/g, '').trim();
  
      // Get the full code from starterCode
      const fullCode = `${code}\n\n${problem.starterCode[selectedLanguage] || ''}`;

      const requestBody = {
        language_id: languages[selectedLanguage].judgeId,
        source_code: fullCode,
        stdin: testCase.input,
        cpu_time_limit: 2,
        cpu_extra_time: 0.5,
        wall_time_limit: 5,
        memory_limit: 128000,
        enable_per_process_and_thread_time_limit: true,
        enable_per_process_and_thread_memory_limit: true,
        base64_encoded: false,
      };
  
      let response;
      try {
        response = await fetch('https://judge0-ce.p.sulu.sh/submissions', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': apiKey
          },
          body: JSON.stringify(requestBody),
        });
      } catch (fetchError) {
        console.error('Fetch error:', fetchError);
        return `Error: Network request failed - ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`;
      }
  
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
  
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Judge0 API error:', { status: response.status, body: errorText });
        return `Error: Judge0 API error: ${response.status} - ${errorText}`;
      }
  
      let result;
      try {
        const responseText = await response.text();
        console.log('Raw response text:', responseText);
        result = JSON.parse(responseText);
        console.log('Parsed Judge0 response:', result);
      } catch (jsonError) {
        console.error('JSON parsing error:', jsonError);
        return 'Error: Failed to parse Judge0 response as JSON';
      }
  
      if (!result || typeof result !== 'object') {
        console.error('Invalid response format:', result);
        return 'Error: Invalid Judge0 response: Expected an object';
      }
  
      if (!result.status || !('id' in result.status)) {
        console.error('Missing or invalid status:', result);
        return 'Error: Invalid Judge0 response: Missing or invalid status field';
      }
  
      if (result.compile_output) {
        return `Compilation error:\n${result.compile_output}\n`;
      }
  
      const statusId = result.status.id;
      const statusDescription = result.status.description || 'Unknown';
      const stdout = result.stdout || '';
      const stderr = result.stderr || '';
  
      console.log('Status ID:', statusId, 'Description:', statusDescription);
  
      if (statusId !== 3) { // 3 = Accepted
        return [
          `Status: ${statusDescription}`,
          stdout ? `Your Output: ${stdout}` : null,
          stderr ? `Error Output: ${stderr}` : null,
          '',
          'Performance:',
          `  Runtime: ${result.time} ms`,
          `  Memory Usage: ${result.memory} KB`,
          ''
        ].filter(Boolean).join('\n');
      }
  
      const actualOutput = stdout.trim();
      const expectedOutput = formattedOutput;
  
      if (actualOutput === expectedOutput) {
        return true;
      } else {
        const inputs = testCase.input.split('\n');
        return [
          'Wrong Answer',
          'Input:',
          ...inputs.map((input, idx) => `  ${idx === 0 ? 'nums' : 'target'}: ${input}`),
          `Expected Output: ${testCase.output}`,
          `Your Output: ${stdout}`,
          '',
          'Performance:',
          `  Runtime: ${result.time} ms`,
          `  Memory Usage: ${result.memory} KB`,
          ''
        ].join('\n');
      }
    } catch (error) {
      console.error('Unexpected error in runTestCase:', error);
      return `Error: Unexpected failure - ${error instanceof Error ? error.message : 'Unknown error'}`;
    }
  };  

  const handleRunCode = async (): Promise<void> => {
    setIsRunning(true);
    setOutput('');
    setFeedback(null);
    setReviewScores(null);

    let lines: string[] = [];
    let outputText = '';
    let passedTests = 0;
    let allTestsPassed = true;

    try {
      // Get the full code from starterCode
      const fullCode = `${code}\n\n${problem.starterCode[selectedLanguage] || ''}`;

      for (let i = 0; i < problem.testCases.length; i++) {
        const testCase = problem.testCases[i];
        // Parse input parameters for better display
        const inputs = testCase.input.split('\n');
        const testLines = [
          `=== Test Case ${i + 1} ===`,
          'Input:',
          ...inputs.map((input, idx) => `  ${idx === 0 ? 'nums' : 'target'}: ${input}`),
          `Expected Output: ${testCase.output}`
        ];
        outputText = [...lines, ...testLines].join('\n');
        setOutput(outputText);

        const result = await runTestCase(fullCode, testCase);
        
        if (result === true) {
          passedTests++;
          lines.push(...testLines, 'Status: Accepted', '');
        } else if (typeof result === 'string') {
          lines.push(...testLines, result);
          allTestsPassed = false;
        }

        outputText = lines.join('\n');
        setOutput(outputText);
      }

      const summaryLines = [
        '',
        '=== Submission Results ===',
        `${passedTests}/${problem.testCases.length} test cases passed.`,
        allTestsPassed ? 'Success: All test cases passed.' : 'Error: Some test cases failed. See details above.'
      ];
      
      lines.push(...summaryLines);
      outputText = lines.join('\n');

      if (allTestsPassed) {
        setActiveTab('feedback');
        await getAIFeedback(code);
      }

      if (allTestsPassed) {
        setActiveTab('feedback');
        await getAIFeedback(code);
      }
    } catch (error) {
      console.error('Error running code:', error);
      setOutput(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setFeedback(null);
    } finally {
      setIsRunning(false);
    }
  };

  const getAIFeedback = async (code: string): Promise<void> => {
    try {
      // Reset previous feedback and scores
      setReviewScores(null);
      
      // Validate code
      if (!code || code.trim() === '') {
        setFeedback("No code to analyze. Please write some code first.");
        return;
      }

      setFeedback("Analyzing your code... Please wait.");
      
      // Include problem information for context
      const fullCode = `
/*
Problem: ${problem.title}
Difficulty: ${problem.difficulty}
Description:
${problem.description}
*/

${code}
      `;

      const scores = await codeReviewService.getCodeReview(fullCode);
      setReviewScores(scores);
      
      // Format feedback with markdown
      const formattedFeedback = `
### Code Review Summary
${scores.summary}

### Recommendations
${scores.recommendations.map(rec => `- ${rec}`).join('\n')}

### Detailed Analysis
${Object.entries(scores.descriptions).map(([category, desc]) => `
#### ${category}
${desc}`).join('\n')}
      `;
      
      setFeedback(formattedFeedback);
    } catch (error) {
      console.error('Error getting AI feedback:', error);
      setFeedback("Sorry, there was an error analyzing your code. Please try again.");
      setReviewScores(null);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white/90">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <motion.button
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={onBack}
            className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors flex items-center border border-white/10"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </motion.button>

          <div className="flex items-center space-x-4">
            <select
              value={selectedLanguage}
              onChange={handleLanguageChange}
              className="px-4 py-2 bg-white/10 text-white rounded-lg border border-white/10 focus:outline-none focus:border-white/20"
            >
              {Object.keys(languages).map((lang) => (
                <option key={lang} value={lang}>
                  {languages[lang as Language].name}
                </option>
              ))}
            </select>

            <motion.button
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsSettingsOpen(!isSettingsOpen)}
              className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors flex items-center border border-white/10"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </motion.button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white/[0.02] p-6 rounded-lg border border-white/10">
              <h2 className="text-2xl font-normal tracking-wide text-white mb-4">{problem.title}</h2>
              <div className="text-sm text-white/60 mb-4">
                Difficulty: <span className="text-white/80">{problem.difficulty}</span>
              </div>
              <div className="prose prose-invert max-w-none">
                <ReactMarkdown components={markdownComponents}>
                  {problem.description}
                </ReactMarkdown>
              </div>
            </div>

            <div className="bg-white/[0.02] p-6 rounded-lg border border-white/10">
              <h3 className="text-xl font-normal tracking-wide text-white mb-4">Examples</h3>
              {problem.examples.map((example, index) => (
                <div key={index} className="mb-6 last:mb-0">
                  <div className="bg-black/30 p-4 rounded-lg border border-white/5 mb-2">
                    <div className="text-sm text-white/60 mb-1">Input:</div>
                    <pre className="text-sm text-white/80 font-mono">{example.input}</pre>
                  </div>
                  <div className="bg-black/30 p-4 rounded-lg border border-white/5">
                    <div className="text-sm text-white/60 mb-1">Output:</div>
                    <pre className="text-sm text-white/80 font-mono">{example.output}</pre>
                  </div>
                  {example.explanation && (
                    <div className="mt-2 text-sm text-white/60">
                      <div className="text-white/60 mb-1">Explanation:</div>
                      <p className="text-white/80">{example.explanation}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-6"
          >
            <div className="bg-white/[0.02] p-6 rounded-lg border border-white/10">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-normal tracking-wide text-white">Code Editor</h3>
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleRunCode}
                  disabled={isRunning}
                  className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors flex items-center border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRunning ? (
                    <>
                      <LoadingIcon />
                      <span className="ml-2">Running...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Run Code
                    </>
                  )}
                </motion.button>
              </div>
              <div className="h-[500px] rounded-lg overflow-hidden border border-white/10">
                <CodeEditor
                  ref={editorRef}
                  code={code}
                  onChange={(value) => setCode(value || '')}
                  language={selectedLanguage}
                  fontSize={editorFontSize}
                  showLineNumbers={showLineNumbers}
                  enableAutoCompletion={enableAutoCompletion}
                  enableSyntaxHighlighting={enableSyntaxHighlighting}
                  problemId={problem.id}
                />
              </div>
            </div>

            <div className="bg-white/[0.02] p-6 rounded-lg border border-white/10">
              <div className="flex space-x-4 mb-4">
                <button
                  onClick={() => setActiveTab('output')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'output'
                      ? 'bg-white/10 text-white border border-white/10'
                      : 'text-white/60 hover:text-white/80'
                  }`}
                >
                  Output
                </button>
                <button
                  onClick={() => setActiveTab('feedback')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'feedback'
                      ? 'bg-white/10 text-white border border-white/10'
                      : 'text-white/60 hover:text-white/80'
                  }`}
                >
                  AI Feedback
                </button>
              </div>
              <div className="bg-black/30 p-4 rounded-lg border border-white/5 min-h-[200px]">
                {activeTab === 'output' ? (
                  <pre className="text-sm text-white/80 font-mono whitespace-pre-wrap">{output}</pre>
                ) : (
                  <div className="text-sm text-white/80">
                    {feedback ? (
                      <ReactMarkdown components={markdownComponents}>{feedback}</ReactMarkdown>
                    ) : (
                      <p className="text-white/60">Run your code to get AI feedback on your solution.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {isSettingsOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/[0.02] p-6 rounded-lg border border-white/10 max-w-md w-full mx-4"
          >
            <h3 className="text-xl font-normal tracking-wide text-white mb-4">Editor Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-white/60 mb-2">Font Size</label>
                <select
                  value={editorFontSize}
                  onChange={(e) => setEditorFontSize(e.target.value)}
                  className="w-full px-4 py-2 bg-white/10 text-white rounded-lg border border-white/10 focus:outline-none focus:border-white/20"
                >
                  <option value="12">12px</option>
                  <option value="14">14px</option>
                  <option value="16">16px</option>
                  <option value="18">18px</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-2">Features</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={showLineNumbers}
                      onChange={(e) => setShowLineNumbers(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-white/80">Show Line Numbers</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={enableAutoCompletion}
                      onChange={(e) => setEnableAutoCompletion(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-white/80">Enable Auto Completion</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={enableSyntaxHighlighting}
                      onChange={(e) => setEnableSyntaxHighlighting(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-white/80">Enable Syntax Highlighting</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsSettingsOpen(false)}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors border border-white/10"
              >
                Close
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PracticeProblem;
